import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { Button, Col, Row, Alert } from "reactstrap";
import { useContext } from "react";
import KYBCompanyContext from "../../../../contexts/KYBCompanyContext";
import { KYBAvailableServices } from "src/modules/3rd-party-management/constants";
import { Accordion } from "react-bootstrap";
import { useQueries } from "@tanstack/react-query";
import KYBService from "src/modules/3rd-party-management/apis/admin/KYBService";
import DNBResult from "./components/DNBResult";
import CreditSafeResult from "./components/CreditSafeResult";
import CompanyInfoResult from "./components/CompanyInfoResult";
import { RiInformationLine } from "react-icons/ri";

const SelectReport = ({
    t,
    onNextButtonClicked,
    onBackButtonClicked,
    onCancelButtonClicked
}) => {
    const {
        supplierData,
        companyData,
        setCompanyData
    } = useContext(KYBCompanyContext);

    const {
        services: selectedServices
    } = companyData;

    const queries = useQueries({
        queries: selectedServices.map((service) => {
            return {
                queryKey: ['3rd-party-management-kyb-search-organizations', supplierData.id, service],
                queryFn: async () => {
                    const kybServiceObj = KYBService.getInstance();

                    return await kybServiceObj.searchOrganizations(supplierData.id, {
                        provider: service
                    });
                },
                refetchOnWindowFocus: false
            }
        })
    });

    const queriesResults = selectedServices.map((service, index) => ({
        id: service,
        ...queries[index]
    }));

    const toggleReport = (serviceId, reportToAdd) => {
        setCompanyData((currentState) => {
            const isSelected = currentState.reports[serviceId]?.findIndex((report) => report.id === reportToAdd.id) > -1;

            let updatedReportsForService;

            if (isSelected) {
                updatedReportsForService = currentState.reports[serviceId].filter((report) => report.id !== reportToAdd.id);
            } else {
                updatedReportsForService = [...(currentState.reports[serviceId] || []), reportToAdd];
            }

            return { ...currentState, reports: { 
                ...currentState.reports,
                [serviceId]: updatedReportsForService
            } };
        });
    }

    const renderSearchResults = (serviceQuery, service, searchColumns) => {
        switch (service.id) {
            case 'dun_brad_street':
                return (
                    <DNBResult
                        serviceQuery={serviceQuery}
                        searchColumns={searchColumns}
                        service={service}
                        toggleReport={toggleReport}
                        companyData={companyData}
                    />
                )
            case 'credit_safe':
                return (
                    <CreditSafeResult
                        serviceQuery={serviceQuery}
                        searchColumns={searchColumns}
                        service={service}
                        toggleReport={toggleReport}
                        companyData={companyData}
                    />
                )
            case 'company_info':
                return (
                    <CompanyInfoResult
                        serviceQuery={serviceQuery}
                        searchColumns={searchColumns}
                        service={service}
                        toggleReport={toggleReport}
                        companyData={companyData}
                    />
                )
            default:
                return null;
        }
    }

    const totalCountOfSelectedReports = companyData.services.reduce((sum, key) => {
        return sum + (companyData.reports[key]?.length || 0);
    }, 0);

    return (
        <Row>
            <Col sm="12">
                <Alert color="info" className="d-flex align-items-center justify-content-start gap-2">
                    <RiInformationLine size={40} />
                    <div>
                        <strong>
                            {t('Select Reports to Retrieve')}
                        </strong>
                        <p className="mb-0">
                            {t('Review the search results for each selected provider below. Expand a provider to see its available reports. To fetch full details in the next step, select the reports you want by checking the box at the start of each row. Only the reports you select will be retrieved, so make sure to choose the ones you need.')}
                        </p>
                    </div>
                </Alert>
            </Col>

            <Col sm='12' className="mb-3 custom-accordion">
                <Accordion alwaysOpen defaultActiveKey={['0', '1', '2']}>
                    {KYBAvailableServices.filter(service => service.enableFetchReport && selectedServices.includes(service.id)).map((service, i) => {
                        const searchColumns = service.searchResultColumns || [];

                        const serviceQuery = queriesResults.find(q => q.id === service.id);

                        return (
                            <Accordion.Item eventKey={i.toString()}>
                                <Accordion.Header>
                                    <div className="d-flex align-items-center w-100 justify-content-between">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="fw-semibold">
                                                {service.title}
                                            </span>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body>
                                    {renderSearchResults(
                                        serviceQuery,
                                        service,
                                        searchColumns
                                    )}
                                </Accordion.Body>
                            </Accordion.Item>
                        )
                    })}
                </Accordion>
            </Col>

            <Col sm='12' className="d-flex justify-content-between">
                <Button
                    color="secondary"
                    outline
                    type="button"
                    onClick={onCancelButtonClicked}
                >
                    {t('Cancel')}
                </Button>

                <div>
                    <Button
                        color="secondary"
                        onClick={onBackButtonClicked}
                        className="me-2"
                        type="button"
                    >
                        {t('Back')}
                    </Button>

                    <Button
                        color="primary"
                        type="button"
                        onClick={onNextButtonClicked}
                        disabled={!totalCountOfSelectedReports}
                    >
                        {t('Next')}
                    </Button>
                </div>
            </Col>
        </Row>
    )
}

const mapStatetoProps = (state) => {
    const { token } = state.Login;
    return {
        user: state.Login.user,
        token: token
    };
};

export default withNamespaces()(
    connect(
        mapStatetoProps,
        {}
    )(SelectReport)
)